
import React, { useState, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Smartphone, Monitor } from "lucide-react"
import { usePreviewMode } from "@/hooks/use-mobile"
import { SiteContent } from "./SiteContent"
import { supabase } from "@/integrations/supabase/client"
import { Course } from "@/types/course"

interface SitePreviewProps {
  username: string | null
  fullName?: string
  bio?: string
  avatarUrl?: string | null
  bannerUrl?: string | null
  socialLinks?: Record<string, string> | null
  email?: string | null
}

export function SitePreview({ 
  username, 
  fullName,
  bio,
  avatarUrl,
  bannerUrl,
  socialLinks,
  email
}: SitePreviewProps) {
  const { previewMode, togglePreviewMode } = usePreviewMode()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(false)
  
  useEffect(() => {
    async function fetchCreatorCourses() {
      if (!username) return;
      
      try {
        setLoading(true);
        
        const { data: profiles, error: profileError } = await supabase
          .from("profiles")
          .select("id")
          .eq("username", username)
          .limit(1);
          
        if (profileError || !profiles || profiles.length === 0) {
          console.error("[SitePreview] Error fetching creator profile:", profileError);
          return;
        }
        
        const creatorId = profiles[0].id;
        
        const { data: coursesData, error: coursesError } = await supabase
          .from("courses")
          .select("*")
          .eq("creator_id", creatorId)
          .eq("published", true)
          .order("created_at", { ascending: false });
          
        if (coursesError) {
          console.error("[SitePreview] Error fetching courses:", coursesError);
          return;
        }
        
        console.log("[SitePreview] Fetched courses:", coursesData?.length || 0, "Course data:", coursesData);
        
        // Add updated_at field to satisfy the Course interface
        const coursesWithUpdates = coursesData?.map(course => ({
          ...course,
          // Safely handle the case where updated_at might not exist
          updated_at: course.created_at // Use created_at as fallback
        })) || [];
        
        setCourses(coursesWithUpdates);
      } catch (error) {
        console.error("[SitePreview] Failed to fetch courses:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchCreatorCourses();
  }, [username]);
  
  if (!username) {
    return (
      <Card className="flex flex-col items-center justify-center h-[600px] bg-muted/30">
        <p className="text-muted-foreground">Set up your username to see your site preview</p>
      </Card>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-muted-foreground">Site Preview</h3>
        <ToggleGroup 
          type="single" 
          value={previewMode} 
          onValueChange={(value) => value && togglePreviewMode()}
          className="inline-flex"
        >
          <ToggleGroupItem value="desktop" aria-label="Desktop preview" title="Desktop preview">
            <Monitor className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="mobile" aria-label="Mobile preview" title="Mobile preview">
            <Smartphone className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className={`w-full overflow-hidden transition-all duration-300 border border-border rounded-lg bg-background`}>
        <div className={`transition-all duration-300 ease-in-out ${previewMode === 'mobile' ? 'max-w-[375px] mx-auto border-x border-border' : 'w-full'}`}>
          <div className={`w-full transition-all duration-300 bg-white ${previewMode === 'mobile' ? 'h-[600px]' : 'h-[600px]'} overflow-auto`}>
            <SiteContent
              fullName={fullName}
              bio={bio}
              avatarUrl={avatarUrl}
              bannerUrl={bannerUrl}
              socialLinks={socialLinks}
              email={email}
              courses={courses}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
